import { Injectable } from '@angular/core';
import { Observable, Subject, from, of } from 'rxjs';
import { CompanyConfig, LOCALSTORAGE_KEYS } from 'src/config';
import { User } from 'src/app/models/verylys';
import { AbstractService, PaginationResult, Populate, PopulateRequest } from '../abstract/abstract.service';
import { AuthService } from '../auth.service';
import { VlApiService } from '../vl-api/vl-api.service';
import { CompanyService } from '../company/company.service';
import { environment } from 'src/environments/environment';
import { switchMap, catchError, tap, take } from 'rxjs/operators';
import { SOCKET_KIND_NOTIFICATION, SocketIOService, SocketUserSessionDeletedNotification } from '../socketio/socketio.service';
import { formalizedItemFromUrl } from 'src/app/utilities/window';
import { Language, languages } from 'src/app/utilities/language';

export interface FederatedIdentity {
    identityProvider: string,
    userId: string,
    userName: string
}

export interface OTP {
    createdAt: Date;
    label: string;
    id: string;
}

export type GeneratedOTP = { totpSecret: string, totpSecretQrCode: string };

@Injectable({
    providedIn: 'root'
})
export class UserService extends AbstractService<User> {
    route: string = "/users/"
    resourceReferenceLabel = "User";

    protected crudMethods = {
        create: false,
        update: true,
        delete: false,
        get: false,
        getAll: false
    }

    protected cacheMethods = {
        get: true,
        getAll: true
    }

    private onSessionDeleted$: Subject<SocketUserSessionDeletedNotification> = new Subject();
    private onLocalStorageCountryChange$: Subject<string> = new Subject();
    private onLocalStorageCurrencyChange$: Subject<string> = new Subject();

    specificSocketMessages: Map<string, Subject<any>> = new Map();

    get onLocalStorageCountryChange(): Observable<string> {
        return this.onLocalStorageCountryChange$.asObservable();
    }

    get onLocalStorageCurrencyChange(): Observable<string> {
        return this.onLocalStorageCurrencyChange$.asObservable();
    }


    get onSessionDeleted(): Observable<SocketUserSessionDeletedNotification> {
        return this.specificSocketMessages?.get(SOCKET_KIND_NOTIFICATION.USER_SESSION_DELETED);
    }

    constructor(public vlapi: VlApiService,
        private authService: AuthService,
        private socketIoService: SocketIOService) {
        super(vlapi, socketIoService);
        this.specificSocketMessages.set(SOCKET_KIND_NOTIFICATION.USER_SESSION_DELETED, this.onSessionDeleted$);
    }

    public update(value: any, ...args) {
        return super.update(value, args).pipe(tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me')))
    }

    /**
     * Get connected user information
     */
    public me(PopulateRequest?: PopulateRequest): Observable<User> {
        return this.vlapi.get<User>(`${this.route}me`, { cache: true, populateRequest: PopulateRequest });
    }

    /**
     * Convert to a bussiness accont
     */
    public convertToBusiness(user: User, tva: string): Observable<any> {
        return this.vlapi.post<any>(`${this.route}convert-to-business`, {
            tva: tva
        }).pipe(tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me')))
    }

    /**
     * Change the currency of a customer
     */
    public changeCurrency(currency: string, emit: boolean = false): Observable<string> {
        return from(this.authService.isLoggedIn())
            .pipe(
                switchMap((isLoggedIn: boolean) => {
                    this.setLocalStorageCurrency(currency, emit);
                    return isLoggedIn ? this.vlapi.post<string>(`${this.route}currency/change`, { currencyId: currency }) : of(null);
                }),
                catchError(error => {
                    throw new Error(error);
                }),
                tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me'))
            );
    }

    /**
     * Change the country of a customer
     */
    public changeCountry(country: string, locale: string, emit = false): Observable<string> {
        return from(this.authService.isLoggedIn())
            .pipe(
                switchMap((isLoggedIn: boolean) => {
                    this.setLocalStorageCountry(country, emit);
                    this.setLocalStorageLocale(locale);
                    return isLoggedIn ? this.vlapi.post<string>(`${this.route}country/change`, { countryId: country }) : of(null);
                }),
                catchError(error => {
                    throw new Error(error);
                }),
                tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me'))
            );

        /*localStorage.setItem(LOCALSTORAGE_KEYS.COUNTRY, country);
        return this.vlapi.post<string>(`${this.route}country/change`, { countryId: country })
          .pipe(tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me')))*/
    }

    /**
     * Change the timezone of a customer
     */
    public changeTimezone(timezoneId: string, timezone: string): Observable<string> {
        localStorage.setItem(LOCALSTORAGE_KEYS.TIMEZONE, timezone);

        return this.vlapi.post<string>(`${this.route}timezone/change`, { timezoneId: timezoneId })
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(this.route + 'me')))
    }

    public setLocalStorageCountry(countryId: string, emit: boolean = false): void {
        const value: string = countryId ?? environment.default_country;
        localStorage.setItem(LOCALSTORAGE_KEYS.COUNTRY, value);

        if (emit) {
            this.onLocalStorageCountryChange$.next(value);
        }
    }

    public setLocalStorageTimezone(timezoneCode: string): void {
        localStorage.setItem(LOCALSTORAGE_KEYS.TIMEZONE, timezoneCode);
    }

    public setLocalStorageLocale(locale: string): void {
        const value: string = locale ?? 'fr';
        localStorage.setItem(LOCALSTORAGE_KEYS.LOCALE, value);
    }

    public setLocalStorageCurrency(currencyId: string, emit: boolean = false): void {
        if (currencyId == null) {
            const { locale } = formalizedItemFromUrl();
            const language = languages.find((language: Language) => language.locale == locale);
            currencyId = language?.currencyId;
        }

        const value: string = currencyId ?? environment.default_currency;
        localStorage.setItem(LOCALSTORAGE_KEYS.CURRENCY, value);

        if (emit) {
            this.onLocalStorageCurrencyChange$.next(value);
        }
    }

    /**
     * Get the currency of a user
     */
    public getCurrencyFromLocalStorage(): string {
        return localStorage.getItem(LOCALSTORAGE_KEYS.CURRENCY);
    }

    /**
     * Get the country of a user
     */
    public getCountryFromLocalStorage(): string {
        return localStorage.getItem(LOCALSTORAGE_KEYS.COUNTRY);
    }

    /**
     * Get the locale of a user
     */
    public getLocaleFromLocalStorage(): string {
        return localStorage.getItem(LOCALSTORAGE_KEYS.LOCALE);
    }

    /**
     * Reset the password of the user
     */
    public resetPassword(): Observable<any> {
        return this.vlapi.post<any>(`${this.route}credentials/reset-password`)
    }

    /**
     * Reset the email of the user
     * This method ask for a validation code sent by email
     * 
     * @param email     New email
     */
    public changeEmailRequest(email: string): Observable<any> {
        const body: any = {
            email: email
        };

        return this.vlapi.post<any>(`${this.route}credentials/change-email-request`, body);
    }

    /**
     * Reset the email of the user
     * 
     * @param email     New email
     * @param code      The validation code
     */
    public changeEmailValidation(email: string, validationCode: string): Observable<any> {
        const body: any = {
            email: email,
            validationCode: validationCode
        };

        return this.vlapi.post<any>(`${this.route}credentials/change-email-validation`, body)
    }

    /**
     * Get user federated identities
     */
    public getFederatedIdentities(): Observable<FederatedIdentity[]> {
        return this.vlapi.get<FederatedIdentity[]>(`${this.route}credentials/federated-identities`, { cache: true });
    }

    /**
     * Create a user federated identities
     * 
     * @param federatedIdentityProvider  The provider of federated identity
     */
    public createFederatedIdentities(federatedIdentityProvider: string, redirectURI: string): Observable<string> {
        const body: any = {
            provider: federatedIdentityProvider,
            redirectURI: redirectURI
        }

        return this.vlapi.post<string>(`${this.route}credentials/federated-identities`, body)
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(`${this.route}credentials/federated-identities`)))
    }

    /**
     * Delete a user federated identities
     * 
     * @param federatedIdentityProvider  The provider of federated identity
     */
    public deleteFederatedIdentities(federatedIdentityProvider: string): Observable<any> {
        return this.vlapi.delete<any>(`${this.route}credentials/federated-identities?providerId=${federatedIdentityProvider}`)
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(`${this.route}credentials/federated-identities`)))
    }

    /**
     * Get user sessions
     */
    public getSessions(): Observable<PaginationResult<any>> {
        return this.vlapi.get<PaginationResult<any>>(`${this.route}sessions`, { cache: true })
    }

    public disconnectSessions(): Observable<null> {
        return this.vlapi.delete<null>(`${this.route}sessions`)
    }

    /**
     * Get the OTP of a user
    */
    public getOTP(): Observable<PaginationResult<OTP>> {
        return this.vlapi.get<PaginationResult<OTP>>(`${this.route}credentials/OTP`, { cache: true });
    }

    /**
     * Generate an OTP
    */
    public generateOTP(): Observable<GeneratedOTP> {
        return this.vlapi.post<GeneratedOTP>(`${this.route}credentials/generate-OTP`)
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(`${this.route}credentials/OTP`)))
    }

    /**
     * Generate an OTP
    */
    public validateOTP(body: { secret: string, deviceName: string, initialCode: string }): Observable<boolean> {
        return this.vlapi.post<boolean>(`${this.route}credentials/validate-OTP`, body)
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(`${this.route}credentials/OTP`)))
    }

    /**
     * Delete the OTP of a user
    */
    public deleteOTP(otp: OTP): Observable<void> {
        return this.vlapi.delete<void>(`${this.route}credentials/OTP?id=${otp.id}`)
            .pipe(tap(() => this.vlapi.invalidateEndpointRequest(`${this.route}credentials/OTP`)))
    }

    public validate(value: any): void {
        throw new Error($localize`:@@error.method_not_implemented:Method not implemented`);
    }
}